import { FormButton, FormInput } from "@/shared/lib/form";
import { loginSchema, type LoginFormData } from "@/shared/lib/validation";
import { useForm } from "@tanstack/react-form";
import React from "react";
import { Link } from "react-router-dom";
import styles from "./LoginForm.module.css";

interface LoginFormProps {
  onSubmit?: (data: LoginFormData) => Promise<void>;
  className?: string;
  isLoading?: boolean;
  error?: string;
}

export const LoginForm: React.FC<LoginFormProps> = ({
  onSubmit,
  className = "",
  isLoading = false,
  error,
}) => {
  const form = useForm({
    defaultValues: {
      email: "",
      password: "",
    },
    onSubmit: async ({ value }) => {
      if (onSubmit) {
        await onSubmit(value);
      }
    },
  });

  return (
    <div className={`${styles.container} ${className}`}>
      <div className={styles.card}>
        <div className={styles.header}>
          <h2 className={styles.title}>Вход в аккаунт</h2>
          <p className={styles.subtitle}>Добро пожаловать в Destiny Games</p>
        </div>

        {error && <div className={styles.error}>{error}</div>}

        <form
          onSubmit={(e) => {
            e.preventDefault();
            e.stopPropagation();
            form.handleSubmit();
          }}
          className={styles.form}
        >
          <form.Field
            name="email"
            validators={{
              onChange: ({ value }) => {
                const result = loginSchema.shape.email.safeParse(value);
                return result.success
                  ? undefined
                  : result.error.errors[0]?.message;
              },
            }}
            children={(field) => (
              <div className={styles.field}>
                <label htmlFor={field.name} className={styles.label}>
                  Email
                </label>
                <FormInput
                  id={field.name}
                  name={field.name}
                  type="email"
                  placeholder="Введите ваш email"
                  value={field.state.value}
                  onChange={(value) => field.handleChange(value)}
                  onBlur={() => field.handleBlur()}
                  hasError={field.state.meta.errors.length > 0}
                  disabled={isLoading}
                  className={styles.input}
                />
                {field.state.meta.errors.length > 0 && (
                  <div className={styles.errorMessage}>
                    {field.state.meta.errors.map((error, index) => (
                      <p key={index} className={styles.errorText}>
                        {String(error)}
                      </p>
                    ))}
                  </div>
                )}
              </div>
            )}
          />

          <form.Field
            name="password"
            validators={{
              onChange: ({ value }) => {
                const result = loginSchema.shape.password.safeParse(value);
                return result.success
                  ? undefined
                  : result.error.errors[0]?.message;
              },
            }}
            children={(field) => (
              <div className={styles.field}>
                <label htmlFor={field.name} className={styles.label}>
                  Пароль
                </label>
                <FormInput
                  id={field.name}
                  name={field.name}
                  type="password"
                  placeholder="Введите пароль"
                  value={field.state.value}
                  onChange={(value) => field.handleChange(value)}
                  onBlur={() => field.handleBlur()}
                  hasError={field.state.meta.errors.length > 0}
                  disabled={isLoading}
                  className={styles.input}
                />
                {field.state.meta.errors.length > 0 && (
                  <div className={styles.errorMessage}>
                    {field.state.meta.errors.map((error, index) => (
                      <p key={index} className={styles.errorText}>
                        {String(error)}
                      </p>
                    ))}
                  </div>
                )}
              </div>
            )}
          />

          <form.Subscribe
            selector={(state) => [state.canSubmit, state.isSubmitting]}
            children={([canSubmit, isSubmitting]) => (
              <FormButton
                type="submit"
                isLoading={isLoading || isSubmitting}
                disabled={!canSubmit}
                className={styles.submitButton}
              >
                Войти
              </FormButton>
            )}
          />
        </form>

        <div className={styles.footer}>
          <p className={styles.footerText}>
            Нет аккаунта?{" "}
            <Link to="/register" className={styles.footerLink}>
              Зарегистрироваться
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
};
